import { Fragment, FormEvent, useEffect, useState } from 'react'
import { useI18n } from '../i18n'
import { navigate } from '../api/client'
import { fetchCycle, type CycleEvent } from '../api/cycle'
import {
  createHolding, deleteHolding, fetchAccount, fetchAssignmentSuggestion, fetchCCCandidates, listHoldings, saveAccount, updateHolding,
  type AccountSnapshot, type AssignmentSuggestion, type CCCandidates, type Holding, type HoldingBucket,
} from '../api/holdings'

type Quote = { price?: number }

const EMPTY_FORM = { symbol: '', qty: '', cost_basis: '', bucket: 'core' as HoldingBucket, event_id: '', thesis: '' }

function AccountCard() {
  const { t } = useI18n()
  const [open, setOpen] = useState(false)
  const [snap, setSnap] = useState<AccountSnapshot | null>(null)
  const [form, setForm] = useState({ net_liq: '', margin_debt: '', reserved_cash: '', maintenance_pct: '' })
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    fetchAccount()
      .then((a) => {
        setSnap(a)
        setForm({
          net_liq: a.net_liq?.toString() ?? '',
          margin_debt: a.margin_debt?.toString() ?? '',
          reserved_cash: a.reserved_cash?.toString() ?? '',
          maintenance_pct: a.maintenance_pct?.toString() ?? '',
        })
      })
      .catch(() => setSnap(null))
  }, [])

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    setSaving(true)
    setError('')
    const num = (v: string) => (v.trim() === '' ? null : Number(v))
    try {
      setSnap(await saveAccount({
        net_liq: num(form.net_liq),
        margin_debt: num(form.margin_debt),
        reserved_cash: num(form.reserved_cash),
        maintenance_pct: num(form.maintenance_pct),
      }))
      setOpen(false)
    } catch (err) {
      setError((err as Error).message)
    } finally {
      setSaving(false)
    }
  }

  const shown = (v?: number | null) => (v == null ? t('holdings.notSet') : v.toLocaleString())

  return (
    <div className="holdings-account">
      <button type="button" className="holdings-account__head" onClick={() => setOpen((v) => !v)}>
        <span>{t('holdings.account.title')}</span>
        <span aria-hidden>{open ? '▾' : '▸'}</span>
      </button>
      {!open && (
        <p className="muted small">
          {t('holdings.account.netLiq')} {shown(snap?.net_liq)} · {t('holdings.account.marginDebt')} {shown(snap?.margin_debt)} ·{' '}
          {t('holdings.account.reservedCash')} {shown(snap?.reserved_cash)} · {t('holdings.account.maintenancePct')} {shown(snap?.maintenance_pct)}
          {snap?.updated_at && ` · ${t('events.asOf').replace('{d}', snap.updated_at.slice(0, 10))}`}
        </p>
      )}
      {open && (
        <form className="position-form" onSubmit={submit}>
          <label>{t('holdings.account.netLiq')}<input type="number" step="0.01" value={form.net_liq} onChange={(e) => setForm({ ...form, net_liq: e.target.value })} /></label>
          <label>{t('holdings.account.marginDebt')}<input type="number" step="0.01" value={form.margin_debt} onChange={(e) => setForm({ ...form, margin_debt: e.target.value })} /></label>
          <label>{t('holdings.account.reservedCash')}<input type="number" step="0.01" value={form.reserved_cash} onChange={(e) => setForm({ ...form, reserved_cash: e.target.value })} /></label>
          <label>{t('holdings.account.maintenancePct')}<input type="number" step="0.01" value={form.maintenance_pct} onChange={(e) => setForm({ ...form, maintenance_pct: e.target.value })} /></label>
          {error && <p className="form-message form-message--error full-row">{error}</p>}
          <button type="submit" className="button button--primary" disabled={saving}>{saving ? t('holdings.saving') : t('holdings.save')}</button>
        </form>
      )}
    </div>
  )
}

function AssignmentModal({ positionId, onClose, onSaved }: {
  positionId: string; onClose: () => void; onSaved: () => void
}) {
  const { t } = useI18n()
  const [suggestion, setSuggestion] = useState<AssignmentSuggestion | null>(null)
  const [cost, setCost] = useState('')
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    fetchAssignmentSuggestion(positionId)
      .then((s) => { setSuggestion(s); setCost(s.cost_basis_suggested.toFixed(2)) })
      .catch((err: Error) => setError(err.message))
  }, [positionId])

  const save = async () => {
    if (!suggestion) return
    setSaving(true)
    setError('')
    try {
      await createHolding({
        symbol: suggestion.symbol,
        qty: suggestion.qty,
        cost_basis: Number(cost),
        bucket: 'core',
        source: 'assignment',
        position_id: positionId,
      })
      onSaved()
      onClose()
    } catch (err) {
      setError((err as Error).message)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="drawer-backdrop" onClick={onClose}>
      <aside className="drawer" onClick={(e) => e.stopPropagation()}>
        <header className="drawer__head">
          <div>
            <p className="kicker">{t('holdings.assign.kicker')}</p>
            <h2>{t('holdings.assign.title')}</h2>
          </div>
          <button type="button" className="drawer__close" onClick={onClose}>×</button>
        </header>
        {error && <p className="form-message form-message--error">{error}</p>}
        {suggestion && (
          <>
            <p className="muted">
              {suggestion.symbol} · {suggestion.qty} {t('holdings.shares')} · {t('holdings.assign.strike')} ${suggestion.strike.toFixed(2)} ·{' '}
              {t('holdings.assign.premiums')} ${suggestion.premiums_total.toFixed(2)}
            </p>
            <label className="holdings-assign__cost">
              {t('holdings.costBasis')}
              <input type="number" step="0.01" value={cost} onChange={(e) => setCost(e.target.value)} />
            </label>
            <div className="holdings-assign__actions">
              <button type="button" className="button button--secondary" onClick={onClose}>{t('holdings.assign.skip')}</button>
              <button type="button" className="button button--primary" onClick={save} disabled={saving}>{saving ? t('holdings.saving') : t('holdings.save')}</button>
            </div>
          </>
        )}
      </aside>
    </div>
  )
}

const usd = (v: number) => `$${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
const usd0 = (v: number) => `$${Math.round(v).toLocaleString()}`
const signedPct = (v: number) => `${v > 0 ? '+' : v < 0 ? '−' : ''}${Math.abs(v).toFixed(1)}%`
const monthDay = (iso: string, language: string) => {
  const d = new Date(`${iso.slice(0, 10)}T00:00:00`)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleDateString(language === 'zh' ? 'zh-CN' : 'en-US', { month: 'short', day: 'numeric' })
}

// CCPanel is decision tree C of the Wheel SOP, inline under the holding:
// covered calls 15–28 DTE with a strike at or above the anchor (assigned
// strike, else cost, else spot). The SOP validated APY-ranked selection for
// cash-secured puts and limit-at-mid fills for the call leg; the ordering
// here is the server's premium ranking, which the footer states plainly.
function CCPanel({ holdingId }: { holdingId: string }) {
  const { language, t } = useI18n()
  const [data, setData] = useState<CCCandidates | null>(null)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    let alive = true
    fetchCCCandidates(holdingId)
      .then((d) => { if (alive) setData(d) })
      .catch(() => { if (alive) setFailed(true) })
    return () => { alive = false }
  }, [holdingId])

  if (failed) return <p className="form-message form-message--error">{t('cc.error')}</p>
  if (!data) return <p className="muted small">{t('cc.loading')}</p>

  const { anchor, holding, rule } = data
  const assigned = anchor.kind === 'assigned_strike'
  const ref = t(assigned ? 'cc.ref.assigned' : 'cc.ref.cost')
  const contracts = Math.floor(holding.qty / 100)

  const depthLine = () => {
    if (anchor.kind === 'spot' || data.depth_pct == null) return t('cc.depth.spot')
    if (data.depth_pct >= 0) {
      return t('cc.depth.above').replace('{amt}', usd(data.spot - anchor.value)).replace('{ref}', ref)
    }
    if (data.depth === 'deep') return t('cc.depth.deep').replace('{ref}', ref)
    return t('cc.depth.shallow')
      .replace('{pct}', `${Math.abs(data.depth_pct).toFixed(1)}%`)
      .replace('{ref}', ref)
  }

  return (
    <div className="holdings-cc">
      <h3>{t('cc.title').replace('{sym}', holding.symbol)}</h3>
      <p className="muted small">
        {t(contracts === 1 ? 'cc.subtitle.one' : 'cc.subtitle')
          .replace('{qty}', String(holding.qty))
          .replace('{n}', String(contracts))}
      </p>
      {data.open_calls.length > 0 && (
        <p className="holdings-cc__open">
          {t(data.open_calls.length === 1 ? 'cc.openCalls.one' : 'cc.openCalls.many')
            .replace('{n}', String(data.open_calls.length))
            .replace('{list}', data.open_calls
              .map((c) => `$${c.strike} · ${monthDay(c.expiry, language)} · ${t('cc.openCalls.dte').replace('{d}', String(c.dte))}`)
              .join(', '))}
        </p>
      )}
      <p className="holdings-cc__facts">
        <span>
          {holding.cost_basis != null
            ? t('cc.costBasis').replace('{v}', usd(holding.cost_basis))
            : t('cc.costBasis.missing')}
        </span>
        {assigned && <span>{t('cc.assignedAt').replace('{v}', usd(anchor.value))}</span>}
        <span>{t(data.spot_source === 'live' ? 'cc.currentPrice' : 'cc.lastClose').replace('{v}', usd(data.spot))}</span>
        {data.depth_pct != null && (
          <span>{t(assigned ? 'cc.vsAssigned' : 'cc.vsCost').replace('{v}', signedPct(data.depth_pct))}</span>
        )}
      </p>
      <p className="muted small">{depthLine()}</p>
      <p className="muted small">
        {t('cc.rule')
          .replace('{min}', String(rule.dte_min))
          .replace('{max}', String(rule.dte_max))
          .replace('{floor}', usd(rule.strike_floor))
          .replace('{oi}', rule.oi_min.toLocaleString())}
      </p>
      {data.candidates.length === 0 ? (
        <p className="holdings-cc__empty">
          {data.chain_available ? t('cc.empty') : t('cc.empty.noChain').replace('{sym}', holding.symbol)}
        </p>
      ) : (
        <ul className="holdings-cc__list">
          {data.candidates.map((c) => (
            <li key={`${c.expiry}-${c.strike}`}>
              <span className="holdings-cc__head">
                {t('cc.row.strike')
                  .replace('{strike}', `$${c.strike}`)
                  .replace('{otm}', signedPct((c.strike - data.spot) / data.spot * 100))
                  .replace('{date}', monthDay(c.expiry, language))
                  .replace('{dte}', String(c.dte))}
              </span>
              <span className="muted small">
                {t('cc.row.premium')
                  .replace('{premium}', usd0(c.premium_per_contract))
                  .replace('{apy}', `${c.apy.toFixed(1)}%`)
                  .replace('{delta}', c.delta.toFixed(2))
                  .replace('{oi}', c.open_interest.toLocaleString())}
              </span>
              <span className="muted small">
                {t('cc.row.ifCalled')
                  .replace('{value}', usd(c.strike + c.mid))
                  .replace('{pct}', signedPct((c.strike + c.mid - anchor.value) / anchor.value * 100))
                  .replace('{ref}', t(`cc.vsRef.${anchor.kind}`))}
              </span>
            </li>
          ))}
        </ul>
      )}
      <p className="muted small holdings-cc__footer">{t('cc.footer')}</p>
    </div>
  )
}

export function HoldingsPanel({ quotes, assignPositionId, onAssignClose }: {
  quotes: Record<string, Quote>
  assignPositionId: string | null
  onAssignClose: () => void
}) {
  const { language, t } = useI18n()
  const [items, setItems] = useState<Holding[]>([])
  const [events, setEvents] = useState<CycleEvent[]>([])
  const [showForm, setShowForm] = useState(false)
  const [editing, setEditing] = useState<Holding | null>(null)
  const [form, setForm] = useState(EMPTY_FORM)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [ccHoldingId, setCcHoldingId] = useState<string | null>(null)

  const load = () => {
    listHoldings().then((r) => setItems(r.items || [])).catch((err: Error) => setError(err.message))
  }

  useEffect(() => { load() }, [])
  useEffect(() => { fetchCycle(45).then((c) => setEvents(c.events || [])).catch(() => setEvents([])) }, [language])

  const startEdit = (h: Holding) => {
    setEditing(h)
    setForm({
      symbol: h.symbol,
      qty: String(h.qty),
      cost_basis: h.cost_basis?.toString() ?? '',
      bucket: h.bucket,
      event_id: h.event_id || '',
      thesis: h.thesis || '',
    })
    setShowForm(true)
  }

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    setSaving(true)
    setError('')
    const body = {
      symbol: form.symbol.toUpperCase(),
      qty: Number(form.qty),
      bucket: form.bucket,
      ...(form.cost_basis.trim() ? { cost_basis: Number(form.cost_basis) } : {}),
      ...(form.event_id ? { event_id: form.event_id } : {}),
      ...(form.thesis.trim() ? { thesis: form.thesis.trim() } : {}),
    }
    try {
      if (editing) await updateHolding(editing.id, body)
      else await createHolding(body)
      setShowForm(false)
      setEditing(null)
      setForm(EMPTY_FORM)
      load()
    } catch (err) {
      setError((err as Error).message)
    } finally {
      setSaving(false)
    }
  }

  const remove = async (h: Holding) => {
    if (!confirm(t('holdings.confirmDelete').replace('{sym}', h.symbol))) return
    try {
      await deleteHolding(h.id)
      setItems((prev) => prev.filter((x) => x.id !== h.id))
    } catch (err) {
      setError((err as Error).message)
    }
  }

  // Three figures on one holding, labelled the same way iOS labels them:
  // value on top, cost basis and unrealized underneath when a cost is on file.
  const valueCell = (h: Holding) => {
    const price = quotes[h.symbol.toUpperCase()]?.price
    if (!Number.isFinite(price)) return '—'
    const value = price! * h.qty
    if (h.cost_basis == null) return usd0(value)
    const cost = h.cost_basis * h.qty
    const pnl = value - cost
    return (
      <>
        {usd0(value)}
        <div className="muted small">
          {t('holdings.row.costBasis').replace('{v}', usd0(cost))}
          {' · '}
          {t('holdings.row.unrealized')
            .replace('{v}', `${pnl < 0 ? '−' : '+'}${usd0(Math.abs(pnl))}`)
            .replace('{pct}', signedPct(cost === 0 ? 0 : (pnl / cost) * 100))}
        </div>
      </>
    )
  }

  return (
    <div className="holdings-panel">
      <div className="holdings-panel__head">
        <h2>{t('holdings.title')}</h2>
        <button type="button" className="button button--primary button--small" onClick={() => { setEditing(null); setForm(EMPTY_FORM); setShowForm((v) => !v) }}>
          {showForm ? t('holdings.cancel') : t('holdings.add')}
        </button>
      </div>

      <AccountCard />

      {error && <p className="form-message form-message--error">{error}</p>}

      {showForm && (
        <form className="position-form" onSubmit={submit}>
          <label>{t('holdings.symbol')}<input required value={form.symbol} onChange={(e) => setForm({ ...form, symbol: e.target.value })} placeholder="AAPL" /></label>
          <label>{t('holdings.qty')}<input required type="number" step="0.0001" min="0.0001" value={form.qty} onChange={(e) => setForm({ ...form, qty: e.target.value })} /></label>
          <label>{t('holdings.costBasis')}<input type="number" step="0.01" value={form.cost_basis} onChange={(e) => setForm({ ...form, cost_basis: e.target.value })} placeholder={t('holdings.optional')} /></label>
          <label>{t('holdings.bucket')}
            <select value={form.bucket} onChange={(e) => setForm({ ...form, bucket: e.target.value as HoldingBucket })}>
              <option value="core">{t('holdings.bucket.core')}</option>
              <option value="event">{t('holdings.bucket.event')}</option>
            </select>
          </label>
          <label>{t('holdings.bindEvent')}
            <select value={form.event_id} onChange={(e) => setForm({ ...form, event_id: e.target.value })}>
              <option value="">{t('holdings.noEvent')}</option>
              {events.map((ev) => <option key={ev.id} value={ev.id}>{ev.label} · {ev.occurs_at.slice(0, 10)}</option>)}
            </select>
          </label>
          <label className="full-row">{t('holdings.thesis')}
            <input value={form.thesis} onChange={(e) => setForm({ ...form, thesis: e.target.value.slice(0, 280) })} placeholder={t('holdings.optional')} maxLength={280} />
          </label>
          <button type="submit" className="button button--primary" disabled={saving}>{saving ? t('holdings.saving') : t('holdings.save')}</button>
        </form>
      )}

      <div className="scanner-table-wrap">
        <table className="scanner-table holdings-table">
          <thead>
            <tr>
              <th className="text-left">{t('holdings.symbol')}</th>
              <th>{t('holdings.qty')}</th>
              <th>{t('holdings.costBasis')}</th>
              <th className="text-left">{t('holdings.bucket')}</th>
              <th>{t('holdings.value')}</th>
              <th className="text-left">{t('holdings.bindEvent')}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr><td colSpan={7} className="empty">{t('holdings.empty')}</td></tr>
            ) : items.map((h) => (
              <Fragment key={h.id}>
              <tr>
                <td className="text-left"><strong>{h.symbol}</strong>{h.thesis && <div className="muted small">{h.thesis}</div>}</td>
                <td>{h.qty}</td>
                <td>{h.cost_basis != null ? `$${h.cost_basis.toFixed(2)}` : '—'}</td>
                <td className="text-left"><span className={`pill pill--${h.bucket === 'core' ? 'blue' : 'amber'}`}>{t(h.bucket === 'core' ? 'holdings.bucket.core' : 'holdings.bucket.event')}</span></td>
                <td>{valueCell(h)}</td>
                <td className="text-left">{events.find((e) => e.id === h.event_id)?.label || '—'}</td>
                <td className="text-right">
                  <a
                    href={`/analyze?symbol=${encodeURIComponent(h.symbol)}`}
                    className="button button--ghost button--small"
                    onClick={(e) => { e.preventDefault(); navigate(`/analyze?symbol=${encodeURIComponent(h.symbol)}`) }}
                  >{t('holdings.check')}</a>
                  <button
                    type="button"
                    className="button button--ghost button--small"
                    onClick={() => setCcHoldingId((prev) => (prev === h.id ? null : h.id))}
                  >{ccHoldingId === h.id ? t('holdings.callCandidates.hide') : t('holdings.callCandidates')}</button>
                  <button
                    type="button"
                    className="button button--ghost button--small"
                    onClick={() => navigate(`/journal?symbol=${encodeURIComponent(h.symbol)}&holding_id=${encodeURIComponent(h.id)}`)}
                  >{t('journal.logDecision')}</button>
                  <button type="button" className="button button--ghost button--small" onClick={() => startEdit(h)}>{t('holdings.edit')}</button>
                  <button type="button" className="button button--ghost button--small" onClick={() => remove(h)}>{t('holdings.delete')}</button>
                </td>
              </tr>
              {ccHoldingId === h.id && (
                <tr>
                  <td colSpan={7}><CCPanel holdingId={h.id} /></td>
                </tr>
              )}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {assignPositionId && (
        <AssignmentModal positionId={assignPositionId} onClose={onAssignClose} onSaved={load} />
      )}
    </div>
  )
}
